<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const isLoading = ref(true);
const deletingId = ref(null);
const templateToDelete = ref(null);
const showDeleteModal = ref(false);
const searchQuery = ref('');
const showSearchInput = ref(false);

const templates = computed(
  () => store.getters['followUpTemplates/getTemplates'] || []
);

const filteredTemplates = computed(() => {
  if (!searchQuery.value) return templates.value;
  const q = searchQuery.value.toLowerCase();
  return templates.value.filter(
    tmpl =>
      tmpl.name?.toLowerCase().includes(q) ||
      tmpl.content?.toLowerCase().includes(q)
  );
});

const handleSearch = () => {
  showSearchInput.value = !showSearchInput.value;
  if (showSearchInput.value) {
    nextTick(() => {
      document.getElementById('templates-search')?.focus();
    });
  } else {
    searchQuery.value = '';
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await store.dispatch('followUpTemplates/fetch');
  } catch {
    useAlert(t('FOLLOW_UP_TEMPLATES.MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
});

const goBack = () => {
  router.push(accountScopedRoute('follow_ups'));
};

const navigateToCreate = () => {
  router.push(accountScopedRoute('follow_up_template_new'));
};

const navigateToEdit = template => {
  router.push(
    accountScopedRoute('follow_up_template_edit', {
      templateId: template.id,
    })
  );
};

const getVariableCount = content => {
  const matches = content?.match(/\{\{[^}]+\}\}/g);
  return matches ? matches.length : 0;
};

const deleteTemplate = async template => {
  deletingId.value = template.id;
  try {
    await store.dispatch('followUpTemplates/delete', template.id);
    useAlert(t('FOLLOW_UP_TEMPLATES.MESSAGES.DELETE_SUCCESS'));
  } catch {
    useAlert(t('FOLLOW_UP_TEMPLATES.MESSAGES.ERROR'));
  } finally {
    deletingId.value = null;
    templateToDelete.value = null;
    showDeleteModal.value = false;
  }
};

const confirmDelete = template => {
  templateToDelete.value = template;
  showDeleteModal.value = true;
};

const closeDelete = () => {
  templateToDelete.value = null;
  showDeleteModal.value = false;
};

const handleConfirmDelete = () => {
  if (templateToDelete.value) {
    deleteTemplate(templateToDelete.value);
  }
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <header
      class="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-background"
    >
      <!-- Left: back + icon chip + title -->
      <div class="flex items-center gap-3 min-w-0">
        <button
          type="button"
          class="p-1.5 rounded-lg hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12 transition-colors flex-shrink-0"
          :title="t('FOLLOW_UP_TEMPLATES.PAGE.BACK')"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-3.5" aria-hidden="true" />
        </button>
        <div
          class="flex size-9 flex-shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <span class="i-lucide-file-text size-4" aria-hidden="true" />
        </div>
        <div class="min-w-0">
          <h1
            class="text-sm font-semibold text-n-slate-12 truncate leading-tight"
          >
            {{ t('FOLLOW_UP_TEMPLATES.PAGE.TITLE') }}
          </h1>
          <p
            class="hidden md:block text-xs text-n-slate-10 truncate leading-tight"
          >
            {{ t('FOLLOW_UP_TEMPLATES.PAGE.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Right actions -->
      <div class="flex items-center gap-1.5">
        <!-- Expandable Search -->
        <div class="flex items-center">
          <div
            class="flex items-center overflow-hidden transition-all duration-200 ease-out"
            :class="showSearchInput ? 'w-48' : 'w-0'"
          >
            <div
              class="h-7 w-full rounded-md border border-n-weak bg-n-alpha-1 flex items-center overflow-hidden transition-colors focus-within:border-n-blue-7 focus-within:bg-n-solid-2"
            >
              <span
                class="i-lucide-search size-3.5 text-n-slate-9 ml-2 flex-shrink-0"
              />
              <input
                id="templates-search"
                v-model="searchQuery"
                type="search"
                class="reset-base w-full bg-transparent border-none outline-none ring-0 m-0 px-2 text-[11px] text-n-slate-12 placeholder:text-n-slate-9 no-margin"
                :placeholder="t('FOLLOW_UP_TEMPLATES.PAGE.SEARCH_PLACEHOLDER')"
                @blur="!searchQuery && (showSearchInput = false)"
              />
            </div>
          </div>
          <NextButton
            variant="ghost"
            color="slate"
            size="xs"
            @click="handleSearch"
          >
            <template #icon>
              <span class="i-lucide-search size-3.5" />
            </template>
          </NextButton>
        </div>

        <div class="w-px h-4 bg-n-weak" />

        <button
          type="button"
          class="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium bg-n-brand text-white hover:opacity-90 transition-opacity"
          @click="navigateToCreate"
        >
          <span class="i-lucide-plus size-3.5" />
          {{ t('FOLLOW_UP_TEMPLATES.PAGE.CREATE') }}
        </button>
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-4">
      <!-- Loading -->
      <div
        v-if="isLoading"
        class="flex flex-col items-center justify-center min-h-[360px]"
      >
        <div
          class="w-8 h-8 border-2 border-n-weak border-t-n-brand rounded-full animate-spin mb-3"
        />
        <p class="text-[11px] text-n-slate-10">
          {{ t('FOLLOW_UP_TEMPLATES.PAGE.LOADING') }}
        </p>
      </div>

      <!-- No search results -->
      <div
        v-else-if="searchQuery && filteredTemplates.length === 0"
        class="flex flex-col items-center justify-center min-h-[360px] text-center px-4"
      >
        <div
          class="flex size-16 items-center justify-center rounded-2xl bg-n-alpha-2 text-n-slate-10 mb-4"
        >
          <span class="i-lucide-search size-8" />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12 mb-1.5">
          {{ t('FOLLOW_UP_TEMPLATES.PAGE.NO_RESULTS_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 max-w-sm">
          {{ t('FOLLOW_UP_TEMPLATES.PAGE.NO_RESULTS_DESCRIPTION') }}
        </p>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="templates.length === 0"
        class="flex flex-col items-center justify-center min-h-[360px] text-center px-4"
      >
        <div
          class="flex size-16 items-center justify-center rounded-2xl bg-n-brand/10 text-n-brand mb-4"
        >
          <span class="i-lucide-file-plus-2 size-8" />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12 mb-1.5">
          {{ t('FOLLOW_UP_TEMPLATES.PAGE.EMPTY_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 max-w-sm mb-4">
          {{ t('FOLLOW_UP_TEMPLATES.PAGE.EMPTY_DESCRIPTION') }}
        </p>
        <button
          type="button"
          class="flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-medium bg-n-brand text-white hover:opacity-90 transition-opacity"
          @click="navigateToCreate"
        >
          <span class="i-lucide-plus size-4" />
          {{ t('FOLLOW_UP_TEMPLATES.PAGE.CREATE_FIRST') }}
        </button>
      </div>

      <!-- Templates Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <div
          v-for="template in filteredTemplates"
          :key="template.id"
          class="group rounded-xl outline outline-1 outline-n-weak bg-n-solid-2 hover:bg-n-alpha-1 transition-all duration-200 overflow-hidden cursor-pointer"
          @click="navigateToEdit(template)"
        >
          <!-- Card Header -->
          <div class="p-3 border-b border-n-weak">
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <h3 class="text-sm font-semibold text-n-slate-12 truncate">
                  {{ template.name }}
                </h3>
                <div class="flex items-center gap-1.5 mt-1">
                  <span
                    class="i-lucide-braces size-3 text-n-slate-10 flex-shrink-0"
                  />
                  <p class="text-xs text-n-slate-10">
                    {{
                      template.uses_items
                        ? t('FOLLOW_UP_BUILDER.CARD.ITEMS_COUNT', {
                            count: template.items_count,
                          })
                        : t('FOLLOW_UP_TEMPLATES.PAGE.VARIABLES_COUNT', {
                            count: getVariableCount(template.content),
                          })
                    }}
                  </p>
                </div>
              </div>
              <span
                class="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0"
                :class="[
                  template.active
                    ? 'bg-emerald-3 text-emerald-11'
                    : 'bg-n-alpha-1 text-n-slate-11',
                ]"
              >
                {{
                  template.active
                    ? t('FOLLOW_UP_TEMPLATES.STATUS.ACTIVE')
                    : t('FOLLOW_UP_TEMPLATES.STATUS.INACTIVE')
                }}
              </span>
            </div>
          </div>

          <!-- Card Body -->
          <div class="p-3">
            <div
              v-if="template.uses_items"
              class="rounded-lg bg-n-alpha-1 px-3 py-2"
            >
              <p class="text-xs text-n-slate-10 italic">
                {{ t('FOLLOW_UP_BUILDER.CARD.SEQUENCE_TEMPLATE') }}
              </p>
            </div>
            <div v-else class="rounded-lg bg-n-alpha-1 px-3 py-2 font-mono">
              <p class="text-xs text-n-slate-11 line-clamp-3">
                {{ template.content }}
              </p>
            </div>
          </div>

          <!-- Card Footer -->
          <div
            class="px-2.5 py-1.5 border-t border-n-weak bg-n-background flex gap-1.5"
          >
            <button
              type="button"
              class="flex flex-1 items-center justify-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium border border-n-weak text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
              @click.stop="navigateToEdit(template)"
            >
              <span class="i-lucide-edit-2 size-3.5" />
              {{ t('FOLLOW_UP_TEMPLATES.PAGE.EDIT') }}
            </button>
            <button
              type="button"
              class="flex flex-1 items-center justify-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium text-n-ruby-11 hover:bg-n-ruby-2 transition-colors"
              :disabled="deletingId === template.id"
              @click.stop="confirmDelete(template)"
            >
              <span
                v-if="deletingId === template.id"
                class="i-lucide-loader-2 size-3.5 animate-spin"
              />
              <span v-else class="i-lucide-trash-2 size-3.5" />
              {{ t('FOLLOW_UP_TEMPLATES.PAGE.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="closeDelete"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl border border-n-weak w-full max-w-sm mx-4"
      >
        <!-- Modal Header -->
        <div
          class="flex items-center justify-between px-4 h-12 border-b border-n-weak"
        >
          <div class="flex items-center gap-2.5">
            <div
              class="flex size-7 items-center justify-center rounded-lg bg-n-ruby-3 text-n-ruby-11"
            >
              <span class="i-lucide-trash-2 size-3.5" aria-hidden="true" />
            </div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('FOLLOW_UP_TEMPLATES.CONFIRM_DELETE_TITLE') }}
            </h3>
          </div>
          <button
            type="button"
            class="text-n-slate-10 hover:text-n-slate-11 p-1 rounded-lg hover:bg-n-alpha-1"
            @click="closeDelete"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
        <!-- Modal Body -->
        <div class="px-4 py-4">
          <p class="text-sm text-n-slate-11">
            {{ t('FOLLOW_UP_TEMPLATES.CONFIRM_DELETE_HINT') }}
          </p>
        </div>
        <!-- Modal Footer -->
        <div class="flex gap-2 px-4 pb-4">
          <button
            type="button"
            class="flex-1 h-9 rounded-lg border border-n-weak text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
            @click="closeDelete"
          >
            {{ t('FOLLOW_UP_TEMPLATES.CONFIRM_DELETE_CANCEL') }}
          </button>
          <button
            type="button"
            class="flex-1 h-9 rounded-lg bg-n-ruby-9 text-sm font-medium text-white hover:bg-n-ruby-10 transition-colors"
            :disabled="deletingId === templateToDelete?.id"
            @click="handleConfirmDelete"
          >
            <span
              v-if="deletingId === templateToDelete?.id"
              class="i-lucide-loader-2 size-4 animate-spin inline-block"
            />
            <span v-else>{{
              t('FOLLOW_UP_TEMPLATES.CONFIRM_DELETE_CONFIRM')
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
